import { Link, useNavigate, useParams } from "react-router-dom";
import { useChangeAddQuestionChapter, useChangeExamStatus, useEditExamInfo, useUpdateExamAssignment, useExamChapterList, useAssignChaptersByAIRag, useExamDetail, useExamQuestionGet, useExamQuestionStatusGet, useSchoolChangeAddQuestion, useSharedNotes, useCreateSharedNote, useAssignSharedNote, useUpdateSharedNote, useDeleteExamQuestion, useGrades, useTags, useUserInfo } from "../../react-query/queries";
import { useAuth } from "../../utils/Auth/AuthProvider";
import { useEffect, useState, useRef, useMemo, useCallback } from "react";
import DOMPurify from 'dompurify';
import RichSlateEditor from './RichSlateEditor'
import katex from 'katex'
import 'katex/dist/katex.min.css'
import renderMathInElement from 'katex/contrib/auto-render'
import { ArrowRight, BarChart3, BookOpen, Check, GraduationCap, Info, Lightbulb, ListChecks, MoreVertical, Pencil, Plus, Printer, Search, Sparkles, StickyNote, Trash2 } from "lucide-react";
import { Label, Modal } from "flowbite-react";
import LaTeXBuilder from '../mathsymbols/LaTeXBuilder'
import AddOtherQuestionsDialog from "./AddOtherQuestionsDialog";
import { usePipelineEngine } from "../../hooks/usePipelineEngine";
import { useGeminiClient } from "../../hooks/useGeminiClient";
import { request as remoteRequest } from "../../utils/remote";
import CollapsedQuestionCard from "./CollapsedQuestionCard";
import ExamAssignmentInfo from "./ExamAssignmentInfo";
import EditAssignmentModal from "./EditAssignmentModal";
import { useOtherSchoolGuard } from "./useOtherSchoolGuard";
import type { ExamAssignmentScope } from "../../school_panel/api/types";
import { useExamDelivery, useExamReport } from "../../school_panel/api/hooks";
import KindBadge from "../../pages/Worksheets/components/KindBadge";
import MarkingCard from "../../pages/Worksheets/components/MarkingCard";
import WorksheetQuestionsPreview from "../../pages/Worksheets/components/WorksheetQuestionsPreview";
import { Badge, Button as KitButton, ConfirmDialog, EmptyState, PageHeader, ShowMoreFooter, Spinner, Tabs, Tag, Track } from "../../school_panel/components/ui";
import ExamQuestionStats from "./ExamQuestionStats";
import { withTime } from '../../school_panel/lib/dates';
import { isOpenQuestion, openQuestionLabel } from '../../school_panel/lib/openQuestion';

/** "Grade 9", "9" and "grade 9" all become "Grade 9"; never "Grade Grade 9". */
function gradeLabel(raw: unknown): string {
    const v = String(raw ?? '').trim().replace(/^grade\s+/i, '');
    return v ? `Grade ${v}` : 'N/A';
}

/** The answer key a question card shows, and whether the AI suggests a different one (one rule for the pill, banner and summary). */
const answerKeyOf = (q: any): string => q?.ans ?? q?.correct_ans ?? q?.ansVal ?? '';
const hasAnswerCorrection = (q: any): boolean => !!q?.corrected_answer && q.corrected_answer !== answerKeyOf(q);

/** Results-release options as the design's three-way segmented control. */
const RELEASE_OPTIONS: { key: 'immediate' | 'after_due' | 'manual'; label: string }[] = [
    { key: 'immediate', label: 'Immediately' }, { key: 'after_due', label: 'After due' }, { key: 'manual', label: 'Manually' },
];
/** The answers (key, review) of a school exam open only after its due time, or, with
 *  no due date, when the teacher presses Release answers; the score follows the mode. */
const releaseNote = (mode: string, dueAt: string | null | undefined, dueText: string | null) => {
    const dueMs = dueAt ? new Date(dueAt).getTime() : NaN;
    if (mode === 'manual') return 'Hidden until you press Release results in the report.';
    if (!dueAt) return 'Score shows on submit; answers open when you press Release answers in the report.';
    if (mode === 'after_due') return `Hidden until ${dueText ?? 'the due time'}, then shown automatically.`;
    if (Number.isNaN(dueMs) || dueMs > Date.now()) return 'Score shows on submit; answers open after the due time.';
    return 'Students see their score and answers as soon as they submit.';
};

const CARD = 'min-w-0 rounded-[20px] border border-line bg-surface p-[22px] shadow-card';
const CARD_TITLE = 'font-display text-[17px] font-bold tracking-[-0.01em] text-ink';
const SECTION_LABEL = 'font-mono text-[11px] font-semibold uppercase tracking-[0.08em] text-muted';
const ICON_BTN = 'inline-grid h-9 w-9 place-items-center rounded-[10px] border border-transparent text-muted transition-colors hover:border-line-strong hover:bg-surface-2 hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent';
const MENU_ITEM = 'flex min-h-[40px] w-full items-center gap-2.5 rounded-[10px] px-3 text-left text-[13px] font-medium text-ink hover:bg-surface-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent';
const MODAL_LABEL = 'font-mono text-[11px] font-semibold uppercase tracking-[0.06em] text-muted';
const INPUT = 'w-full rounded-[12px] border border-line-strong bg-surface text-sm text-ink focus:border-slate focus:ring-2 focus:ring-accent';
const CHECKBOX = 'h-5 w-5 flex-none cursor-pointer rounded-[6px] border-line-strong text-navy focus:ring-2 focus:ring-accent';

/** Overview tile: mono label over a display-font value. */
function InfoTile({ label, children, className = '' }: { label: string; children: React.ReactNode; className?: string }) {
    return (
        <div className={`min-w-0 rounded-[14px] border border-track bg-panel p-3.5 ${className}`}>
            <p className={SECTION_LABEL}>{label}</p>
            <div className="mt-1.5 break-words font-display text-[17px] font-bold text-ink">{children}</div>
        </div>
    );
}

/** On/off switch over a real checkbox, so existing change handlers and labels keep working. */
function Switch({ checked, onChange, label, onNavy = false }: { checked: boolean; onChange: (e: React.ChangeEvent<HTMLInputElement>) => void; label: string; onNavy?: boolean }) {
    return (
        <label className="relative inline-flex flex-none cursor-pointer items-center">
            <input type="checkbox" role="switch" aria-label={label} checked={checked} onChange={onChange} className="peer sr-only" />
            <span aria-hidden className={`h-[26px] w-[46px] rounded-full transition-colors after:absolute after:left-[3px] after:top-[3px] after:h-5 after:w-5 after:rounded-full after:bg-white after:shadow after:transition-[left] after:content-[''] peer-checked:bg-success peer-checked:after:left-[23px] peer-focus-visible:ring-2 peer-focus-visible:ring-accent ${onNavy ? 'bg-navy-3' : 'bg-switch-off'}`} />
        </label>
    );
}


const ExamDetailComponent = () => {
    const { token, role } = useAuth();
    const { data: userInfo } = useUserInfo();
    const { examId } = useParams();
    const navigate = useNavigate();
    // Paper exams (plan B Task 3): switch this exam to paper delivery and send
    // the teacher to the paper-exams list to print the answer sheet next.
    const examDeliveryMut = useExamDelivery();
    const [useOnPaperError, setUseOnPaperError] = useState<string | null>(null);
    const handleUseOnPaper = async () => {
        if (!examId) return;
        setUseOnPaperError(null);
        try {
            await examDeliveryMut.mutateAsync({ examId: Number(examId), delivery: 'paper' });
            navigate('/exams/paper', { state: { notice: 'Now a paper exam. Print the answer sheet next.' } });
        } catch {
            setUseOnPaperError('Could not switch this exam to paper. Try again.');
        }
    };

    // Data fetching hooks
    const { status: examStatus, data: examData, error: examError, isFetching: examIsFetching } = useExamDetail(token, examId)
    // A superuser is asked once before the first change to another school's exam.
    const { guard: otherSchoolGuard, needsConfirm: otherSchoolNeedsConfirm, dialog: otherSchoolDialog } = useOtherSchoolGuard();
    const guardedExam = examId ? { id: examId, owner_school: (examData as any)?.exam?.owner_school ?? null } : null;
    const guarded = <A extends unknown[]>(fn: (...args: A) => unknown) =>
        (...args: A) => otherSchoolGuard(guardedExam, () => { void fn(...args); });
    // B-017: national exams owned by another institution are view-only.
    const examInstitution = (examData as any)?.exam?.institution;
    // The server says whether this viewer may edit (Hub admins edit every Hub exam); older servers: compare institutions.
    const serverEditable: boolean | undefined = (examData as any)?.exam?.editable
    const isSharedForeign = role !== 'superuser' && (serverEditable !== undefined ? !serverEditable : (
        examInstitution?.id != null
        && userInfo?.institution_id != null
        && Number(examInstitution.id) !== Number(userInfo.institution_id)));
    // Owner rule: a teacher changes only exams they created; marking and the report stay open.
    const notMine = !isSharedForeign && (examData as any)?.exam?.can_edit === false;
    const isReadOnly = isSharedForeign || notMine;
    // The read-only gate is only trustworthy once both the exam and the user's institution are known.
    const gateReady = examStatus === 'success' && !!(examData as any)?.exam && (role === 'superuser' || userInfo != null);
    const cardReadOnly = !gateReady || isReadOnly;
    const examKind: string = (examData as any)?.exam?.kind ?? 'exam';
    const isNewKind = examKind !== 'exam';
    // The edit-info endpoint (name, time, tag) is school-admin only (IsLegacySchoolAdmin).
    const canEditInfo = role === 'superuser' || role === 'school_admin' || role === 'school' || role === 'admin';
    // Assignment edits go through school/exams/<id>/, open to teachers (the server checks their audience).
    const canEditAssignment = canEditInfo || role === 'teacher';
    const sharedInstitutionName: string = (examData as any)?.exam?.school_name || examInstitution?.name || 'another institution';
    const { data: tagsData } = useTags();
    const { data: gradesData } = useGrades(token);
    const { isFetching: isStatFetching, data: statData } = useExamQuestionStatusGet(token, examId)
    const { data: questionsData, isFetching: questionIsFetching, isError: questionsIsError, refetch: refetchQuestions } = useExamQuestionGet(token, examId)
    const { data: sharedNotesData } = useSharedNotes();
    // The same cached report the Statistics tab reads: took / assigned / average for the header, publishing card and subtitle.
    const { data: reportData } = useExamReport(Number(examId) > 0 ? Number(examId) : null, { section: 'all' });

    // Mutation hooks
    const { mutateAsync: schoolExamStatusChange, isPending: isExamStatusChanging } = useChangeExamStatus(examId!)
    const { mutateAsync: schoolQuestionChapterChange } = useChangeAddQuestionChapter(examId!)
    const { mutateAsync: schoolExamQuestionChange } = useSchoolChangeAddQuestion(examId!)
    const { mutateAsync: editExamInfoMut, isPending: isEditing } = useEditExamInfo(examId!)
    const { mutateAsync: updateAssignmentMut, isPending: isSavingAssign } = useUpdateExamAssignment()
    const deleteQuestionMut = useDeleteExamQuestion(examId!);
    const createSharedNoteMut = useCreateSharedNote();
    const updateSharedNoteMut = useUpdateSharedNote();
    const assignSharedNoteMut = useAssignSharedNote(examId!);

    // Removed unused role variable
    const [questionsArray, setQuestionsArray] = useState<any[]>([]);
    const [statusSwitch, setStatusSwitch] = useState(false);
    const [chapterEdit, setChapterEdit] = useState(false);
    const [questionChapterChanged, setquestionChapterChanged] = useState<{ [code: number]: string; }>({});
    const [openModal, setOpenModal] = useState(false);
    const [openVerificationModal, setOpenVerificationModal] = useState(false);
    const [chapterGradeModalOpen, setChapterGradeModalOpen] = useState(false);
    const [selectedGradeIds, setSelectedGradeIds] = useState<number[]>([]);
    const [appliedGradeIds, setAppliedGradeIds] = useState<number[]>([]);
    const [editInfoOpen, setEditInfoOpen] = useState(false);
    const [assignOpen, setAssignOpen] = useState(false);
    const [editName, setEditName] = useState<string>('');
    const [editMinutes, setEditMinutes] = useState<string>('');
    const [editTag, setEditTag] = useState<string>('');
    const [selectedQuestion, setSelectedQuestion] = useState<{
        ques: string,
        choice_a: string,
        choice_b: string,
        choice_c: string,
        choice_d: string,
        ans: string,
        id?: number | string,
        explanation?: string,
        reference?: string,
    }>({
        ques: '',
        choice_a: '',
        choice_b: '',
        choice_c: '',
        choice_d: '',
        ans: '',
    })

    const [selectedOption, setSelectedOption] = useState<string>('');
    // Removed unused isOptionSelected
    const [showMathBuilder, setShowMathBuilder] = useState(false)
    const [expandedQuestions, setExpandedQuestions] = useState<{ [id: string]: { explanation: boolean, sources: boolean } }>({});

    const toggleExpansion = (id: string, type: 'explanation' | 'sources') => {
        setExpandedQuestions(prev => ({
            ...prev,
            [id]: {
                ...prev[id],
                [type]: !prev[id]?.[type]
            }
        }));
    };

    const containerRef = useRef<HTMLDivElement | null>(null)

    // Shared note state
    const [selectedQuestionIds, setSelectedQuestionIds] = useState<number[]>([]);
    const [sharedNoteModalOpen, setSharedNoteModalOpen] = useState(false);
    const [newSharedNote, setNewSharedNote] = useState({ title: '', content: '' });
    const [existingSharedNoteId, setExistingSharedNoteId] = useState<string>('');


    const [addQuestionsOpen, setAddQuestionsOpen] = useState(false);
    const [editSharedNoteModalOpen, setEditSharedNoteModalOpen] = useState(false);
    const [editingSharedNote, setEditingSharedNote] = useState<{ id: number; title: string; content: string } | null>(null);

    const openEditSharedNote = (note: any) => {
        // eslint-disable-next-line no-console
        
        let contentValue = '';
        if (note.content != null) {
            if (typeof note.content === 'string') {
                contentValue = note.content;
            } else if (typeof note.content === 'object') {
                // Try common fields, then stringify if all else fails
                contentValue = note.content.content || note.content.html || note.content.text || JSON.stringify(note.content);
            } else {
                contentValue = String(note.content);
            }
        }
        
        // ensure valid HTML wrapping for RichSlateEditor if it looks like plain text
        if (contentValue && !contentValue.trim().startsWith('<')) {
            contentValue = `<p>${contentValue}</p>`;
        }

        setEditingSharedNote({ 
            id: note.id, 
            title: note.title || '', 
            content: contentValue
        });
        setEditSharedNoteModalOpen(true);
    };

    const handleUpdateSharedNote = async () => {
        if (editingSharedNote) {
            await updateSharedNoteMut.mutateAsync({
                id: editingSharedNote.id,
                title: editingSharedNote.title,
                content: editingSharedNote.content,
            });
            await refetchQuestions();
            setEditSharedNoteModalOpen(false);
            setEditingSharedNote(null);
        }
    };

    const toggleQuestionSelection = (id: number) => {
        setSelectedQuestionIds(prev =>
            prev.includes(id) ? prev.filter(qid => qid !== id) : [...prev, id]
        );
    };

    const handleAssignSharedNote = async () => {
        let noteId = Number(existingSharedNoteId);
        if (!noteId && newSharedNote.title && newSharedNote.content) {
            const created = await createSharedNoteMut.mutateAsync(newSharedNote);
            noteId = created.id;
        }

        if (noteId && selectedQuestionIds.length > 0) {
            await assignSharedNoteMut.mutateAsync({
                note_id: noteId,
                question_ids: selectedQuestionIds,
            });
            await refetchQuestions();
            setSharedNoteModalOpen(false);
            setSelectedQuestionIds([]);
            setNewSharedNote({ title: '', content: '' });
            setExistingSharedNoteId('');
        }
    };

    const [questionProcessingMode, setQuestionProcessingMode] = useState(false);
    const [allRags, setAllRags] = useState<any[]>([]);
    const [isLoadingRags, setIsLoadingRags] = useState(false);
    const [useOptions, setUseOptions] = useState<boolean | null>(null);
    const [bookPickerOpen, setBookPickerOpen] = useState(false);
    const [bookPickerIndices, setBookPickerIndices] = useState<number[]>([]);
    const [selectedBookIds, setSelectedBookIds] = useState<string[]>([]);

    // The AI runs on the server (aiBatchGenerate, metered per user and school): no personal key needed.
    const pipeline = usePipelineEngine(null);
    const { 
        questions: pipelineQuestions, 
        progress: pipelineProgress, 
        runPipeline, 
        cancel, 
        retryFailed, 
        retryNoContextAllBooks, 
        retrySpecificBooks, 
        useAIKnowledge,
        reassignChapter,
        searchByCourse,
        aiAssistedGeneration,
        detectChapterType,
        setQuestions: setPipelineQuestions
    } = pipeline;

    // Unsaved changes warning
    useEffect(() => {
        const hasUnsaved = pipelineQuestions.some(q => q._isNewResult);
        
        const handleBeforeUnload = (e: BeforeUnloadEvent) => {
            if (hasUnsaved) {
                e.preventDefault();
                e.returnValue = '';
            }
        };

        const handleInternalNavigation = (e: MouseEvent) => {
            if (!hasUnsaved) return;
            
            const target = e.target as HTMLElement;
            const anchor = target.closest('a');
            
            // If it's an internal link (same origin) and not a hash link or target=_blank
            if (anchor && anchor instanceof HTMLAnchorElement && 
                anchor.origin === window.location.origin && 
                !anchor.hash && 
                anchor.target !== '_blank'
            ) {
                if (!window.confirm("You have unsaved AI results. Are you sure you want to leave? Your changes will be lost.")) {
                    e.preventDefault();
                    e.stopPropagation();
                }
            }
        };

        window.addEventListener('beforeunload', handleBeforeUnload);
        // Intercept clicks on links for internal navigation
        document.addEventListener('click', handleInternalNavigation, true);

        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
            document.removeEventListener('click', handleInternalNavigation, true);
        };
    }, [pipelineQuestions]);

    // Load RAGs
    const courseIds = useMemo(() => {
        const exam = (examData as any)?.exam;
        if (!exam) return [];
        const ids: string[] = [];
        
        // Add all specific courses from the courses array
        if (Array.isArray(exam.courses) && exam.courses.length > 0) {
            exam.courses.forEach((c: any) => {
                if (c.id) ids.push(String(c.id));
            });
        }
        
        // Add the primary course if present
        if (exam.course?.id) {
            ids.push(String(exam.course.id));
        }

        // Only include field of study if no specific courses are found
        if (ids.length === 0 && exam.field_of_study?.id) {
            ids.push(String(exam.field_of_study.id));
        }
        
        return Array.from(new Set(ids));
    }, [examData]);

    // University exams read their courses' textbooks; a school subject exam reads every grade's
    // school textbooks for its subject (type=school, so a superuser outside a school gets them too).
    const schoolSubjectId: number | null = (examData as any)?.exam?.school_subject_id ?? null;
    useEffect(() => {
        if (!token || !questionProcessingMode || (!courseIds.length && !schoolSubjectId)) return;
        setIsLoadingRags(true);
        (async () => {
            try {
                const qp = new URLSearchParams();
                if (courseIds.length) {
                    qp.set('course_ids', courseIds.join(','));
                } else {
                    qp.set('type', 'school');
                    qp.set('subject_id', String(schoolSubjectId));
                }
                const data = await remoteRequest(`/api/textbook/list/?${qp.toString()}`, { headers: { Authorization: `Bearer ${token}` } });
                setAllRags(data?.books || []);
            } catch (e) {
                setAllRags([]);
            } finally {
                setIsLoadingRags(false);
            }
        })();
    }, [token, courseIds, schoolSubjectId, questionProcessingMode]);

    // Detect chapter type
    useEffect(() => {
        if (!token || useOptions !== null || !questionProcessingMode) return;
        // A school subject exam has no courses: its chapters are the subject's own chapters, not chapter options.
        if (!courseIds.length) {
            if ((examData as any)?.exam) setUseOptions(false);
            return;
        }
        (async () => {
            try {
                const detected = await detectChapterType(courseIds);
                if (detected === 'chapter_option') setUseOptions(true);
                else if (detected === 'chapter') setUseOptions(false);
                else setUseOptions(false); // Default
            } catch {
                setUseOptions(false);
            }
        })();
    }, [token, courseIds, useOptions, detectChapterType, questionProcessingMode, examData]);

    const hasInitializedPipeline = useRef(false);
    useEffect(() => {
        if (questionProcessingMode && questionsData?.questions && !hasInitializedPipeline.current) {
            hasInitializedPipeline.current = true;
            const sourceArr: any[] = (questionsData as any).full || questionsData.questions || [];
            const pqs = sourceArr.map((q, i) => {
                const choiceA = q.choice_a ?? q.option1 ?? q.choiceA ?? '';
                const choiceB = q.choice_b ?? q.option2 ?? q.choiceB ?? '';
                const choiceC = q.choice_c ?? q.option3 ?? q.choiceC ?? '';
                const choiceD = q.choice_d ?? q.option4 ?? q.choiceD ?? '';
                const choiceE = q.choice_e ?? q.option5 ?? q.choiceE ?? '';
                
                const choices = [choiceA, choiceB, choiceC, choiceD, choiceE].filter(c => c !== undefined && c !== null && c !== '');
                
                return {
                    ...q,
                    question: q.ques || q.question_html || '',
                    choices,
                    _status: (q.explanation || q.corrected_answer) ? 'explanation_done' : 'pending',
                    _retries: 0,
                    _course_id: q.course_id || null,
                    _ragHits: [],
                };
            });
            setPipelineQuestions(pqs as any);
        } else if (!questionProcessingMode) {
            hasInitializedPipeline.current = false;
        }
    }, [questionProcessingMode, questionsData, setPipelineQuestions]);

    const handleSearchSpecificBooks = useCallback((index: number) => {
        setBookPickerIndices([index]);
        setSelectedBookIds([]);
        setBookPickerOpen(true);
    }, []);

    const handleBookPickerConfirm = useCallback(() => {
        if (bookPickerIndices.length === 0 || selectedBookIds.length === 0) return;
        setBookPickerOpen(false);
        retrySpecificBooks(bookPickerIndices, selectedBookIds, allRags);
        setBookPickerIndices([]);
        setSelectedBookIds([]);
    }, [bookPickerIndices, selectedBookIds, allRags, retrySpecificBooks]);


    // Render LaTeX expressions inside an HTML string using KaTeX, then sanitize.
    const renderLatexInHtml = (html: string) => {
        if (!html) return ''
        try {
            let out = String(html)
            out = out.replace(/\$\$([\s\S]+?)\$\$/g, (_m, expr) => {
                try { return katex.renderToString(expr, { displayMode: true, throwOnError: false }) } catch (e) { return _m }
            })
            out = out.replace(/\$(?!\$)([^$]+?)\$/g, (_m, expr) => {
                try { return katex.renderToString(expr, { displayMode: false, throwOnError: false }) } catch (e) { return _m }
            })
            try { return DOMPurify.sanitize(out) } catch (e) { return out }
        } catch (e) {
            try { return DOMPurify.sanitize(String(html)) } catch (err) { return String(html) }
        }
    }

    // Chapter grade filter popup state
    const [gradeOptions, setGradeOptions] = useState<{ id: number; name: string }[]>([]);


    const getFilteredChapters = (): any[] => {
        const chapters: any[] = (chapterListData as any)?.chapters || [];
        
        // If we are using the AI API (appliedGradeIds has values), 
        // the backend already filtered by grades, so we return as is.
        if (appliedGradeIds && appliedGradeIds.length > 0) {
            return chapters;
        }

        // Legacy filtering for the full institution chapter list
        if (selectedGradeIds && selectedGradeIds.length > 0) {
            const filtered = chapters.filter(c => {
                // Support various backend field names for grade ID(s)
                const cgIds: any[] = [];
                if (Array.isArray(c.school_grade_ids)) cgIds.push(...c.school_grade_ids);
                if (Array.isArray(c.school_grades)) cgIds.push(...c.school_grades.map((g: any) => g.id || g));
                if (c.grade_id != null) cgIds.push(c.grade_id);

                return cgIds.some((cgId: any) => 
                    selectedGradeIds.some(sgId => String(sgId) === String(cgId))
                );
            });
            return filtered;
        }
        return chapters;
    }


    // Switch between default and AI RAG chapters based on whether grades are applied
    const { data: chapterListDataLegacy, isFetching: chapterListIsFetchingLegacy } = useExamChapterList(token, examId, chapterEdit && appliedGradeIds.length === 0)
    const { data: chapterListDataAI, isFetching: chapterListIsFetchingAI } = useAssignChaptersByAIRag(token, examId, appliedGradeIds, chapterEdit && appliedGradeIds.length > 0)

    const chapterListData = appliedGradeIds.length > 0 ? chapterListDataAI : chapterListDataLegacy;
    const chapterListIsFetching = appliedGradeIds.length > 0 ? chapterListIsFetchingAI : chapterListIsFetchingLegacy;

    // Removed unused subjectName

    useEffect(() => {
        if (chapterGradeModalOpen && gradesData?.grades) {
            const opts = gradesData.grades.map((g: any) => ({
                id: g.id,
                name: g.grade_name || g.name
            }));
            setGradeOptions(opts);
            // If nothing selected yet, default to all
            if (selectedGradeIds.length === 0) {
                setSelectedGradeIds(opts.map((o: any) => o.id));
            }
        }
    }, [chapterGradeModalOpen, gradesData]);

    const changingExamStatus = async (event: React.FormEvent) => {
        event.preventDefault();
        const next = !statusSwitch;
        await schoolExamStatusChange({ toBeValidated: token, exam_id: examId!, status: next ? 'Active' : 'Draft' }, {
            onSuccess() {
                // no-op
            },
        } as any);
        setStatusSwitch(next);
    }

    const questionEdit = (ind: number) => {
        // Use the same source array that we used when rendering the questions list
        // (some responses include a `full` array with richer objects)
        const sourceArr: any[] = (questionsData && (questionsData as any).full) ? (questionsData as any).full : (questionsData && questionsData.questions ? questionsData.questions : []);
        const q = sourceArr[ind]
        // dev: log incoming question shape to help debug object rendering
        // eslint-disable-next-line no-console
        // helper: if value is a string and doesn't contain a block tag, wrap in <p>..</p>
        const wrapWithParagraph = (val: any) => {
            if (val == null) return ''
            // if it's already a string
            if (typeof val === 'string') {
                const s = val.trim()
                // look for common block-level tags at start
                if (/^<\/?(p|h1|h2|h3|h4|ul|ol|blockquote|div|li)\b/i.test(s)) return s
                // otherwise wrap
                return `<p>${s}</p>`
            }
            // if it's an object, try to find nested string fields
            try {
                if (typeof val === 'object') {
                    for (const key of ['ques', 'question_html', 'html', 'content', 'text', 'value']) {
                        if (typeof (val as any)[key] === 'string') {
                            return wrapWithParagraph((val as any)[key])
                        }
                    }
                }
            } catch (e) {
                // fallthrough
            }
            // fallback to string conversion wrapped
            return `<p>${String(val)}</p>`
        }

        // normalize fields to the editor shape and ensure wrapping
        const normalized = {
            id: q.id ?? q.question ?? undefined,
            ques: wrapWithParagraph(q.ques ?? q.question_html ?? ''),
            choice_a: wrapWithParagraph(q.option1 ?? q.choice_a ?? q.choiceA ?? ''),
            choice_b: wrapWithParagraph(q.option2 ?? q.choice_b ?? q.choiceB ?? ''),
            choice_c: wrapWithParagraph(q.option3 ?? q.choice_c ?? q.choiceC ?? ''),
            choice_d: wrapWithParagraph(q.option4 ?? q.choice_d ?? q.choiceD ?? ''),
            ans: q.ans ?? q.correct_ans ?? q.corrected_answer ?? '',
            // ensure explanation is valid HTML for the RichSlateEditor
            explanation: wrapWithParagraph(q.explanation ?? ''),
            // reference is plain text (may contain '||' separators)
            reference: q.reference != null ? String(q.reference) : '',
            // Not sent back: only decides whether the A–D editors show.
            _open: isOpenQuestion(q),
        }
    // eslint-disable-next-line no-console
    setSelectedQuestion(normalized as any)
        // setSelectedOption should be the raw answer value (e.g. 'A'|'B'|'C'|'D'),
        // not the HTML-wrapped version used for the editor's ans field.
        const rawAns = (q?.ans ?? q?.correct_ans ?? q?.corrected_answer ?? '').toString().trim()
        setSelectedOption(rawAns)
        // Removed unused setSelectedIndex
        setOpenModal(true)
    }

    const changingChpater = async (event: React.FormEvent) => {
        setOpenVerificationModal(false)
        event.preventDefault();

        schoolQuestionChapterChange({ toBeValidated: token, exam_id: examId!, data: questionChapterChanged }, {
            onSuccess(data) {
                if (data['stat'] == 'done') {
                    setquestionChapterChanged({})
                }
            },
        })


    }

    const changeChapterOnUpdate = (chap: string, ques: number) => {
        setquestionChapterChanged(prevState => ({
            ...prevState,
            [ques]: chap
        }))
    }

    const changeSetChapterEdit = () => {
        const next = !chapterEdit;
        setChapterEdit(next)
        if (next) {
            setChapterGradeModalOpen(true);
        } else {
            setChapterGradeModalOpen(false);
        }
    }

    const toggleGrade = (id: number) => {
        setSelectedGradeIds(prev =>
            prev.includes(id) ? prev.filter(gid => gid !== id) : [...prev, id]
        );
    }

    const selectAllGrades = () => {
        setSelectedGradeIds(gradeOptions.map(o => o.id));
    }

    const clearAllGrades = () => {
        setSelectedGradeIds([]);
    }

    // Refresh questions when grades are applied to update the available chapters
    useEffect(() => {
        if (chapterEdit) {
            refetchQuestions();
        }
    }, [appliedGradeIds, chapterEdit, refetchQuestions]);

    const openEditInfo = () => {
        const nm = ((examData as any)?.exam?.name) ?? '';
        const tm = ((examData as any)?.exam?.time) ?? '';
        const tg = ((examData as any)?.exam?.tag?.id) ?? '';
        setEditName(nm as string);
        setEditMinutes(tm !== null && tm !== undefined ? String(tm) : '');
        setEditTag(tg !== null && tg !== undefined ? String(tg) : '');
        setEditInfoOpen(true);
    }

    const saveEditInfo = async () => {
        const payload: any = {};
        if (editName && editName.trim().length > 0) payload.name = editName.trim();
        if (editMinutes !== '') {
            const n = Number(editMinutes);
            if (!Number.isNaN(n) && n >= 0) payload.time_minutes = n;
        }
        payload.tag_id = editTag !== '' ? Number(editTag) : null;
        await editExamInfoMut({ toBeValidated: token, exam_id: examId!, payload });
        setEditInfoOpen(false);
    }

    const openAssignEdit = () => setAssignOpen(true);

    const questionEditChange = (event: string, name: string) => {
        setSelectedQuestion((prevFormData) => ({
            ...prevFormData,
            [name]: event,
        }));
    };

    const saveQuestionEdit = async (event: React.FormEvent) => {
        setOpenModal(false)
        event.preventDefault();

        schoolExamQuestionChange({ toBeValidated: token, exam_id: examId!, data: { ...selectedQuestion, _open: undefined } as any }, {
            onSuccess(data) {
                },
        })
        setOpenModal(false)


    }

    const [questionToDelete, setQuestionToDelete] = useState<number | string | null>(null);
    const handleDeleteQuestion = (questionId: number | string) => {
        setQuestionToDelete(questionId);
    }
    const confirmDeleteQuestion = async () => {
        if (questionToDelete == null) return;
        const id = questionToDelete;
        setQuestionToDelete(null);
        await deleteQuestionMut.mutateAsync({ question_id: id });
    }


    // Removed unused changeTextColor


    useEffect(() => {
        if (!examIsFetching && statData && typeof (statData as any).active === 'boolean') {
            setStatusSwitch(!!(statData as any).active);
        }
    }, [statData, examIsFetching]);

    const handleSearchSpecificBooksBulk = useCallback((indices: number[]) => {
        setBookPickerIndices(indices);
        setSelectedBookIds([]);
        setBookPickerOpen(true);
    }, []);

    // Each reassigned chapter is saved on the question; reload the list once any is.
    const handleReassignChapterBulk = useCallback(async (indices: number[]) => {
        if (useOptions === null) return;
        const saved = await Promise.all(indices.map(i => reassignChapter(i, courseIds, useOptions, allRags, examId)));
        if (saved.some(Boolean)) refetchQuestions();
    }, [courseIds, useOptions, allRags, reassignChapter, examId, refetchQuestions]);

    const handleSearchByCourseBulk = useCallback((indices: number[]) => {
        if (useOptions === null) return;
        searchByCourse(indices, courseIds, useOptions, allRags, examId || null);
    }, [courseIds, useOptions, allRags, searchByCourse, examId]);

    const handleAIAssistedGenerationBulk = useCallback((indices: number[]) => {
        aiAssistedGeneration(indices, allRags);
    }, [allRags, aiAssistedGeneration]);

    const [isSavingAll, setIsSavingAll] = useState(false);

    const handleSaveAISuggestion = async (index: number) => {
        const q = pipelineQuestions[index];
        if (!q) return;

        const updated: any = {
            id: q.id,
            explanation: q.explanation || '',
            ans: q.corrected_answer || q.ans || q.answer || '',
            reference: Array.isArray(q.references) ? q.references.join('||') : (q.references || ''),
            corrected_answer: "",
        };

        if (q.corrected_answer && q.corrected_answer.trim().length === 1) {
            const label = q.corrected_answer.trim().toUpperCase();
            if (['A', 'B', 'C', 'D', 'E'].includes(label)) {
                updated.ans = label;
            }
        }

        await schoolExamQuestionChange({ toBeValidated: token, exam_id: examId!, data: updated });
        hasInitializedPipeline.current = false;
        await refetchQuestions();
    };

    const handleSaveAllAISuggestions = async () => {
        const toSave = pipelineQuestions.filter(q => q._isNewResult);
        if (toSave.length === 0) return;

        setIsSavingAll(true);
        try {
            for (const q of toSave) {
                const updated: any = {
                    id: q.id,
                    explanation: q.explanation || '',
                    ans: q.corrected_answer || q.ans || q.answer || '',
                    reference: Array.isArray(q.references) ? q.references.join('||') : (q.references || ''),
                    corrected_answer: "",
                };
                if (q.corrected_answer && q.corrected_answer.trim().length === 1) {
                    const label = q.corrected_answer.trim().toUpperCase();
                    if (['A', 'B', 'C', 'D', 'E'].includes(label)) updated.ans = label;
                }
                await schoolExamQuestionChange({ toBeValidated: token, exam_id: examId!, data: updated });
            }
            hasInitializedPipeline.current = false;
            await refetchQuestions();
        } finally {
            setIsSavingAll(false);
        }
    };

    const ProcessingActionMenu = ({ index }: { index: number }) => {
        const [menuOpen, setMenuOpen] = useState(false);
        const menuRef = useRef<HTMLDivElement>(null);

        useEffect(() => {
            const handleClickOutside = (e: MouseEvent) => {
                if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false)
            }
            if (menuOpen) document.addEventListener('mousedown', handleClickOutside)
            return () => document.removeEventListener('mousedown', handleClickOutside)
        }, [menuOpen]);

        return (
            <div className="relative" ref={menuRef}>
                <button 
                    type="button"
                    onClick={(e) => { e.stopPropagation(); setMenuOpen(!menuOpen); }}
                    aria-label="More AI actions"
                    aria-haspopup="menu"
                    aria-expanded={menuOpen}
                    className={ICON_BTN}
                >
                    <MoreVertical size={18} strokeWidth={1.8} aria-hidden />
                </button>
                {menuOpen && (
                    <div role="menu" aria-label="More AI actions" className="absolute right-0 top-full z-[60] mt-1 min-w-[220px] rounded-[14px] border border-line bg-surface p-1.5 shadow-card">
                        <button type="button" onClick={(e) => { e.stopPropagation(); setMenuOpen(false); retryNoContextAllBooks([index], allRags); }}
                            role="menuitem" className={MENU_ITEM}>
                            <Search size={18} strokeWidth={1.8} aria-hidden /> Search all books
                        </button>
                        <button type="button" onClick={(e) => { e.stopPropagation(); setMenuOpen(false); handleSearchSpecificBooks(index); }}
                            role="menuitem" className={MENU_ITEM}>
                            <BookOpen size={18} strokeWidth={1.8} aria-hidden /> Search specific books
                        </button>
                        <button type="button" onClick={(e) => { e.stopPropagation(); setMenuOpen(false); useAIKnowledge([index]); }}
                            role="menuitem" className={MENU_ITEM}>
                            <GraduationCap size={18} strokeWidth={1.8} aria-hidden /> Use AI knowledge
                        </button>
                        <button type="button" onClick={(e) => { e.stopPropagation(); setMenuOpen(false); void handleReassignChapterBulk([index]); }}
                            role="menuitem" className={MENU_ITEM}>
                            <Lightbulb size={18} strokeWidth={1.8} aria-hidden /> Reassign chapter
                        </button>
                    </div>
                )}
            </div>
        );
    };


    useEffect(() => {
        if (!questionIsFetching) {
            setQuestionsArray([ ])
            // Use pipelineQuestions as source if in processing mode, else use data from API
            const sourceArr: any[] = questionProcessingMode 
                ? pipelineQuestions 
                : ((questionsData && (questionsData as any).full) ? (questionsData as any).full : (questionsData?.questions || []));
            
            sourceArr.forEach((element: any, key: number) => {
                const choiceA = element.choice_a ?? element.option1 ?? element.choiceA ?? '';
                const choiceB = element.choice_b ?? element.option2 ?? element.choiceB ?? '';
                const choiceC = element.choice_c ?? element.option3 ?? element.choiceC ?? '';
                const choiceD = element.choice_d ?? element.option4 ?? element.choiceD ?? '';
                const ansVal = answerKeyOf(element);
                // Open questions (short answer marked by the teacher on paper) have no choices.
                const open = isOpenQuestion(element);

                const isProcessing = element._processing;
                const status = element._status;
                const correctedAnswer = element.corrected_answer;
                const reference = (Array.isArray(element.references) && element.references.length > 0) 
                    ? element.references.join('||') 
                    : (element.reference || '');

                setQuestionsArray(prevArray => [...prevArray,
                <article key={element.id || key} aria-label={`Question ${key + 1}`} className={`mb-3.5 rounded-[18px] border bg-surface p-[18px] transition-[border-color,box-shadow] ${selectedQuestionIds.includes(element.id) ? 'border-slate ring-[3px] ring-mist' : 'border-line'} ${isProcessing ? 'animate-pulse opacity-70' : ''}`}>
                    <div className="mb-3 flex flex-wrap items-center gap-2.5">
                        {!cardReadOnly && (
                            <input
                                type="checkbox"
                                aria-label={`Select question ${key + 1}`}
                                checked={selectedQuestionIds.includes(element.id)}
                                onChange={() => toggleQuestionSelection(element.id)}
                                className="h-[22px] w-[22px] flex-none cursor-pointer rounded-[7px] border-[1.5px] border-line-strong bg-surface text-navy focus:ring-2 focus:ring-accent focus:ring-offset-0"
                            />
                        )}
                        <span className="font-mono text-[13px] font-semibold text-muted">Q{key + 1}</span>
                        {open
                            ? <Badge tone="neutral">{openQuestionLabel(element)}</Badge>
                            : <Badge tone="info" className="!text-ink">{ansVal ? `Ans: ${ansVal}` : 'No answer'}</Badge>}

                        {questionProcessingMode && hasAnswerCorrection(element) && (
                            <Badge tone="danger">Answer flagged</Badge>
                        )}

                        {questionProcessingMode && status && (
                            <Badge tone={status === 'explanation_done' ? 'success' : status === 'no_context' ? 'warning' : status === 'failed' ? 'danger' : 'neutral'}>
                                {status === 'explanation_done' ? 'AI verified' : status === 'no_context' ? 'No context' : status === 'pending' ? 'Pending AI' : status}
                            </Badge>
                        )}

                        {element.shared_note_details && !cardReadOnly && (
                            <button
                                type="button"
                                onClick={() => openEditSharedNote(element.shared_note_details)}
                                className="inline-flex max-w-full items-center gap-1 truncate rounded-[6px] bg-ground px-[7px] py-0.5 font-mono text-[11px] font-semibold tracking-[0.02em] text-muted-2 hover:bg-mist hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
                            >
                                <StickyNote size={14} strokeWidth={1.8} aria-hidden /> Note: {element.shared_note_details.title}
                            </button>
                        )}

                        <span className="flex-1" />

                        {cardReadOnly ? null : questionProcessingMode ? (
                            <div className="flex items-center gap-1.5">
                                {(status === 'explanation_done' || status === 'no_context') && (
                                    <KitButton size="sm" variant="secondary" onClick={guarded(() => handleSaveAISuggestion(key))}>
                                        Save AI suggestion
                                    </KitButton>
                                )}
                                <ProcessingActionMenu index={key} />
                            </div>
                        ) : (
                            <div className="flex items-center gap-1">
                                <button type="button" onClick={guarded(() => questionEdit(key))} className={ICON_BTN} title="Edit question" aria-label={`Edit question ${key + 1}`}>
                                    <Pencil size={18} strokeWidth={1.8} aria-hidden />
                                </button>
                                <button type="button" onClick={guarded(() => handleDeleteQuestion(element.id))} className={`${ICON_BTN} hover:border-danger-line hover:bg-danger-bg hover:text-danger-ink`} title="Delete question" aria-label={`Delete question ${key + 1}`}>
                                    <Trash2 size={18} strokeWidth={1.8} aria-hidden />
                                </button>
                            </div>
                        )}
                    </div>

                    {questionProcessingMode && hasAnswerCorrection(element) && (
                        <div className="mb-3 flex items-center gap-2.5 rounded-[12px] bg-warning-bg px-3 py-2.5 text-[13px] text-warning-ink">
                            <Lightbulb size={18} strokeWidth={1.8} className="flex-none" aria-hidden />
                            <span><strong>AI correction detected.</strong> Answer key would change from {ansVal || 'none'} to {correctedAnswer}. Review before saving.</span>
                        </div>
                    )}

                    <div className="prose mb-3.5 max-w-none text-ink dark:prose-invert">
                        <div className="text-base font-medium leading-relaxed" dangerouslySetInnerHTML={{ __html: renderLatexInHtml(element.ques || '') }} />
                    </div>

                    {((element as any).pendingImagePreview) ? (
                        <div className="mb-3.5 overflow-hidden rounded-[14px] border border-line">
                            <img src={String((element as any).pendingImagePreview)} alt={`preview-${key}`} className="h-auto max-w-full" />
                        </div>
                    ) : (element.image ? (
                        <div className="mb-3.5 overflow-hidden rounded-[14px] border border-line">
                            <img src={String(element.image)} alt={`question-${key}`} className="h-auto max-w-full" />
                        </div>
                    ) : null)}

                    {open ? (
                        <p className="rounded-[12px] border border-dashed border-line px-3 py-[9px] text-sm text-muted">
                            {element.graded_by_teacher
                                ? 'Answered in writing on the paper; the teacher marks it.'
                                : 'Answered in writing; no choices.'}
                        </p>
                    ) : (
                    <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                        {[
                            { label: 'A', content: choiceA },
                            { label: 'B', content: choiceB },
                            { label: 'C', content: choiceC },
                            { label: 'D', content: choiceD }
                        ].map((opt, i) => (
                            <div key={i} className={`flex min-w-0 items-center gap-2.5 rounded-[12px] border px-3 py-[9px] ${ansVal === opt.label ? 'border-slate bg-note' : 'border-line'}`}>
                                <span className={`grid h-[26px] w-[26px] flex-none place-items-center rounded-[8px] font-mono text-xs font-semibold ${ansVal === opt.label ? 'bg-slate text-white' : 'bg-surface-2 text-muted-2'}`}>
                                    {opt.label}
                                </span>
                                {ansVal === opt.label && <span className="sr-only">(correct answer)</span>}
                                <div className="min-w-0 break-words text-sm text-ink" dangerouslySetInnerHTML={{ __html: renderLatexInHtml(opt.content || '') }} />
                            </div>
                        ))}
                    </div>
                    )}

                    <div className="mt-3.5 flex flex-wrap items-center gap-2.5 border-t border-hairline pt-3 text-[13px]">
                        {chapterEdit && !chapterListIsFetching ? (
                            <>
                                <label className="flex min-h-[38px] min-w-0 items-center gap-2 rounded-[12px] border border-line-strong bg-surface px-3 focus-within:ring-2 focus-within:ring-accent">
                                    <span className="sr-only">Chapter for question {key + 1}</span>
                                    <select
                                        className="min-w-0 max-w-full border-0 bg-transparent py-1 pl-0 pr-7 text-[13px] text-ink focus:ring-0 sm:min-w-[220px]"
                                        value={element.chapter_id?.toString() || '-1'}
                                        onChange={(e) => changeChapterOnUpdate(e.target.value, element.id)}
                                    >
                                        <option value='-1'>Not selected</option>
                                        {getFilteredChapters().map((item: any, idx: number) => (
                                            <option key={idx} value={item.id}>
                                                {item.unit ? `Unit ${item.unit}: ` : ''}{item.name}{item.grade_name ? ` - ${item.grade_name}` : ''}
                                            </option>
                                        ))}
                                    </select>
                                </label>
                                {Object.keys(questionChapterChanged).includes(`${element.id}`) && (
                                    <Badge tone="warning">Unsaved change</Badge>
                                )}
                            </>
                        ) : (
                            <>
                                <span className="text-muted">Chapter</span>
                                {(() => {
                                    const isOpt = !!element.is_chapteroption;
                                    const chapOpt = element.chapter_option_name ?? element.chapter_option ?? null;
                                    const subOpt = element.subchapter_option_name ?? element.subchapter_option ?? null;
                                    const name = isOpt ? (chapOpt || element.chapter_name) : element.chapter_name;
                                    const sub = isOpt ? subOpt : element.subchapter_name;

                                    if (name) {
                                        return (
                                            <>
                                                <span className="font-semibold text-ink">{name}</span>
                                                {sub && <span className="text-muted">/ {sub}</span>}
                                            </>
                                        );
                                    }
                                    return <Badge tone="danger">Not assigned</Badge>;
                                })()}
                            </>
                        )}

                        <span className="flex-1" />

                        {(element.explanation || reference) && (
                            <button
                                type="button"
                                aria-expanded={!!expandedQuestions[element.id]?.explanation}
                                onClick={() => toggleExpansion(element.id, 'explanation')}
                                className="inline-flex min-h-[32px] items-center text-[13px] font-semibold text-muted-2 underline-offset-[3px] hover:text-ink hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
                            >
                                {expandedQuestions[element.id]?.explanation
                                    ? (element.explanation ? 'Hide explanation' : 'Hide sources')
                                    : (element.explanation ? 'Show explanation' : 'Show sources')}
                            </button>
                        )}
                    </div>

                    {expandedQuestions[element.id]?.explanation && (element.explanation || reference) && (
                        <div className="mt-3 rounded-[12px] bg-panel px-3.5 py-3 text-[13px] text-muted-2">
                            {element.explanation && (
                                <div className="prose prose-sm max-w-none text-muted-2 dark:prose-invert">
                                    <strong className="text-ink">Explanation.</strong>
                                    <div dangerouslySetInnerHTML={{ __html: renderLatexInHtml(element.explanation) }} />
                                </div>
                            )}
                            {reference && reference.split('||').map((ref: string, i: number) => (
                                <span key={i} className={`block break-words text-xs ${element.explanation || i > 0 ? 'mt-1.5' : ''}`}>
                                    Source: <span dangerouslySetInnerHTML={{ __html: renderLatexInHtml(ref.trim()) }} />
                                </span>
                            ))}
                        </div>
                    )}
                </article>])
            })
            }
    }, [questionIsFetching, chapterEdit, chapterListIsFetching, questionChapterChanged, selectedQuestionIds, expandedQuestions, questionProcessingMode, pipelineQuestions, isReadOnly, gateReady])
    
    // The Questions tab shows the first few cards; the footer expands to all of them.
    const [showAllQuestions, setShowAllQuestions] = useState(false);
    const QUESTIONS_PREVIEW = 5;
    // After questions render, run KaTeX auto-render to catch any inline/display math we didn't replace
    useEffect(() => {
        try {
            if (containerRef.current) {
                renderMathInElement(containerRef.current, {
                    delimiters: [
                        { left: '$$', right: '$$', display: true },
                        { left: '$', right: '$', display: false },
                        { left: '\\(', right: '\\)', display: false },
                        { left: '\\[', right: '\\]', display: true }
                    ],
                    throwOnError: false,
                })
            }
        } catch (e) {
            }
    }, [questionsArray, showAllQuestions])
    // ---- Presentation-only values, derived from data already loaded above ----
    const [detailTab, setDetailTab] = useState<'questions' | 'stats'>('questions');
    const exam: any = (examData as any)?.exam;
    const examLoaded = examStatus === 'success' && !!exam;
    const instType = exam?.institution?.inst_type;
    const isUniversity = instType === 'university' || instType === 'college';
    // isReadOnly is only known once the exam has loaded, so every edit control waits for it.
    const canEditExam = gateReady && !isReadOnly && !isNewKind;
    const questionCount: number = questionsData?.questions?.length || 0;
    const questionSource: any[] = (questionsData as any)?.full || questionsData?.questions || [];
    const chapterNameOf = (q: any) => (q?.is_chapteroption ? (q.chapter_option_name ?? q.chapter_option ?? q.chapter_name) : q?.chapter_name) || null;
    const withoutChapter = questionSource.filter((q) => !chapterNameOf(q)).length;
    // Open questions (marked by the teacher) have no key to set.
    const withoutAnswer = questionSource.filter((q) => !isOpenQuestion(q) && !(q?.ans ?? q?.correct_ans ?? q?.ansVal)).length;
    const chapterCount = new Set(questionSource.map(chapterNameOf).filter(Boolean)).size;
    const countsLine = [
        `${questionCount} question${questionCount === 1 ? '' : 's'}`,
        withoutChapter > 0 ? `${withoutChapter} without a chapter` : null,
        questionSource.length === 0 ? null : withoutAnswer > 0 ? `${withoutAnswer} without an answer key` : 'answer key set for all',
    ].filter(Boolean).join(' · ');
    const contextParts: string[] = (isUniversity
        ? [exam?.field_of_study?.name, exam?.academic_year?.name]
        : [(exam?.school_grade_name || exam?.grade) ? gradeLabel(exam?.school_grade_name ?? exam?.grade) : null, exam?.subject]
    ).filter(Boolean);
    const dueText: string | null = exam?.due_at
        ? withTime(exam.due_at, { weekday: 'short', day: 'numeric', month: 'short' })
        : (exam?.due_date || null);
    const takenHeader = reportData?.header;
    const takenLine = takenHeader && takenHeader.assigned_count > 0
        ? `${takenHeader.took_count} of ${takenHeader.assigned_count} students have taken it`
        : null;
    const summaryLine = examLoaded ? [
        `${questionCount} question${questionCount === 1 ? '' : 's'}`,
        exam.time ? `${exam.time} minutes` : null,
        dueText ? `due ${dueText}` : null,
        takenLine,
    ].filter(Boolean).join(' · ') : undefined;
    // updateExamAssignment resolves (not throws) on request errors; only a result with `exam` is a success. The
    // control reads the saved value, so a failed change leaves the previous option selected.
    const [releaseError, setReleaseError] = useState<string | null>(null);
    const changeRelease = async (mode: 'immediate' | 'after_due' | 'manual') => {
        setReleaseError(null);
        try {
            const res: any = await updateAssignmentMut({ exam_id: examId!, payload: { results_release: mode } });
            if (res && !res.exam) setReleaseError('Could not change when students see results. Try again.');
        } catch {
            setReleaseError('Could not change when students see results. Try again.');
        }
    };
    const releaseMode: string | undefined = ['immediate', 'after_due', 'manual'].includes(exam?.results_release) ? exam.results_release : undefined;
    const dayMonth = (iso: unknown) => {
        const d = iso ? new Date(String(iso)) : null;
        return d && !Number.isNaN(d.getTime()) ? d.toLocaleDateString(undefined, { day: 'numeric', month: 'short' }) : null;
    };
    const createdLine = (() => {
        const on = dayMonth(exam?.created_at ?? exam?.created);
        const edited = dayMonth(exam?.updated);
        const by = exam?.creator_name ? `Created by ${exam.creator_name}${on ? ` on ${on}` : ''}` : (exam?.created ? `Created ${exam.created}` : null);
        return [by, edited ? `last edited ${edited}` : null].filter(Boolean).join(' · ') || null;
    })();
    // The section with the lowest share of its students having sat the exam (needs the per-section header).
    const behindLine = (() => {
        const secs = (takenHeader?.sections ?? []).filter((x) => x.assigned > 0);
        if (secs.length < 2) return null;
        const worst = secs.reduce((a, b) => (b.took / b.assigned < a.took / a.assigned ? b : a));
        return `${worst.display || worst.name} is furthest behind (${worst.took} of ${worst.assigned})`;
    })();
    const aiCounts = {
        verified: pipelineQuestions.filter((q: any) => q._status === 'explanation_done').length,
        noContext: pipelineQuestions.filter((q: any) => q._status === 'no_context').length,
        corrections: pipelineQuestions.filter(hasAnswerCorrection).length,
    };
    const aiSummary = [
        aiCounts.verified > 0 ? `${aiCounts.verified} verified` : null,
        aiCounts.noContext > 0 ? `${aiCounts.noContext} no context` : null,
        aiCounts.corrections > 0 ? `${aiCounts.corrections} answer correction${aiCounts.corrections === 1 ? '' : 's'}` : null,
    ].filter(Boolean).join(' · ');
    const deliveryLabel = exam?.delivery === 'paper' ? 'Paper delivery' : exam?.delivery === 'app' ? 'Online delivery' : null;
    const selectedCount = selectedQuestionIds.length;

    return (
        <>
            <LaTeXBuilder show={showMathBuilder} onClose={() => setShowMathBuilder(false)} initialValue={''} onInsert={(tex) => {
                try {
                    const cur = String(selectedQuestion?.ques || '')
                    // append as inline math by default
                    const appended = cur + (cur && !cur.endsWith(' ') ? ' ' : '') + `$${tex}$`
                    questionEditChange(appended, 'ques')
                } catch (e) {
                    // fallback
                    questionEditChange((selectedQuestion?.ques || '') + `$${tex}$`, 'ques')
                }
                setShowMathBuilder(false)
            }} />
            <Modal size="4xl" show={openModal} onClose={() => setOpenModal(false)} popup>
                <Modal.Header>
                    <div className="flex items-center gap-3 p-4">
                        <span className="grid h-10 w-10 place-items-center rounded-[12px] bg-mist text-slate">
                            <Pencil size={18} strokeWidth={1.8} aria-hidden />
                        </span>
                        <h3 className="font-display text-xl font-bold text-ink">
                            Edit question
                        </h3>
                    </div>
                </Modal.Header>
                <Modal.Body>
                    <div className="space-y-8 p-2">
                        <div className="space-y-4">
                            <div className="flex items-center justify-between">
                                <Label htmlFor="ques" value="Question content" className={MODAL_LABEL} />
                                <button type="button" className="inline-flex items-center gap-1 text-[13px] font-semibold text-muted-2 hover:text-ink hover:underline" onClick={() => setShowMathBuilder(true)}>
                                    <Plus size={14} strokeWidth={1.8} aria-hidden /> Insert math
                                </button>
                            </div>
                            <div className="overflow-hidden rounded-[14px] border border-line">
                                <RichSlateEditor initialValue={selectedQuestion?.ques} onChange={(html) => { questionEditChange(html, 'ques') }} />
                            </div>
                        </div>

                        {(selectedQuestion as any)?._open ? (
                            <p className="rounded-[12px] bg-surface-2 px-4 py-3 text-sm text-muted-2">
                                This question is answered in writing, so it has no options or answer key.
                            </p>
                        ) : (
                        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                            {[
                                { id: 'choice_a', label: 'Option A', value: selectedQuestion?.choice_a },
                                { id: 'choice_b', label: 'Option B', value: selectedQuestion?.choice_b },
                                { id: 'choice_c', label: 'Option C', value: selectedQuestion?.choice_c },
                                { id: 'choice_d', label: 'Option D', value: selectedQuestion?.choice_d }
                            ].map((opt) => (
                                <div key={opt.id} className="space-y-2">
                                    <Label htmlFor={opt.id} value={opt.label} className={MODAL_LABEL} />
                                    <div className="overflow-hidden rounded-[12px] border border-line bg-surface-2">
                                        <RichSlateEditor initialValue={opt.value} onChange={(html) => { questionEditChange(html, opt.id) }} />
                                    </div>
                                </div>
                            ))}
                        </div>
                        )}

                        <div className="grid grid-cols-1 items-start gap-8 md:grid-cols-3">
                            <div className="space-y-3" hidden={!!(selectedQuestion as any)?._open}>
                                <Label value="Correct answer" className={MODAL_LABEL} />
                                <div className="grid grid-cols-4 gap-2">
                                    {['A', 'B', 'C', 'D'].map((val) => (
                                        <button
                                            key={val}
                                            type="button"
                                            aria-pressed={selectedOption === val}
                                            onClick={() => {
                                                questionEditChange(val, 'ans');
                                                setSelectedOption(val);
                                            }}
                                            className={`h-12 rounded-[12px] border font-mono font-semibold transition-colors ${selectedOption === val ? 'border-navy bg-navy text-white dark:border-mist-fixed dark:bg-mist-fixed dark:text-navy' : 'border-line-strong bg-surface text-muted-2 hover:bg-surface-2'}`}
                                        >
                                            {val}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            <div className="space-y-4 md:col-span-2">
                                <div className="space-y-2">
                                    <Label value="Explanation" className={MODAL_LABEL} />
                                    <div className="overflow-hidden rounded-[12px] border border-line bg-surface-2">
                                        <RichSlateEditor initialValue={selectedQuestion?.explanation} onChange={(html) => questionEditChange(html, 'explanation')} />
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    <Label value="Reference or sources" className={MODAL_LABEL} />
                                    <textarea
                                        value={selectedQuestion?.reference || ''}
                                        onChange={(e) => questionEditChange(e.target.value, 'reference')}
                                        rows={3}
                                        className={`${INPUT} p-4`}
                                        placeholder="Add sources, separate with ||"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </Modal.Body>
                <Modal.Footer className="flex justify-end gap-3 border-t border-line p-6">
                    <KitButton variant="ghost" onClick={() => setOpenModal(false)}>
                        Cancel
                    </KitButton>
                    <KitButton onClick={(e) => saveQuestionEdit(e)}>
                        Save question
                    </KitButton>
                </Modal.Footer>
            </Modal>

            <PageHeader
                title={examLoaded && exam.name ? String(exam.name) : 'Exam detail'}
                eyebrow={
                    <>
                        <Link to="/exams/exam-list" className="text-muted hover:text-ink hover:underline">Exam list</Link>
                        {contextParts.length > 0 && <span> / {contextParts.join(' · ')}</span>}
                    </>
                }
                subtitle={summaryLine}
                actions={
                    <>
                        {canEditExam && canEditInfo && (
                            <KitButton variant="ghost" icon={<Pencil size={18} strokeWidth={1.8} aria-hidden />} onClick={guarded(openEditInfo)}>
                                Edit exam info
                            </KitButton>
                        )}
                        {examLoaded && !isReadOnly && (
                            <KitButton variant="ghost" icon={<Printer size={18} strokeWidth={1.8} aria-hidden />} onClick={guarded(handleUseOnPaper)} disabled={examDeliveryMut.isPending}>
                                {examDeliveryMut.isPending ? 'Working…' : 'Use on paper'}
                            </KitButton>
                        )}
                        <Link to={`/exams/report/${examId}`} className="inline-flex min-h-[44px] items-center justify-center gap-2 rounded-[12px] border border-navy bg-navy px-4 py-2 text-sm font-semibold text-white transition hover:bg-navy-2 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent">
                            <BarChart3 size={18} strokeWidth={1.8} aria-hidden /> Open report
                        </Link>
                        {canEditExam && (
                            <KitButton icon={<Plus size={18} strokeWidth={1.8} aria-hidden />} onClick={guarded(() => setAddQuestionsOpen(true))}>
                                Add questions
                            </KitButton>
                        )}
                    </>
                }
            />

            {useOnPaperError && (
                <p role="alert" className="mb-[18px] rounded-[12px] bg-danger-bg px-4 py-3 text-sm text-danger-ink">{useOnPaperError}</p>
            )}

            {isNewKind && !isReadOnly && (
                <p className="mb-[18px] rounded-[14px] border border-line bg-surface-2 px-4 py-3 text-sm text-ink">
                    This {examKind} is edited in the worksheet editor.{' '}
                    <Link to={`/worksheets/${examId}/edit`} onClick={(e) => {
                        if (otherSchoolNeedsConfirm(guardedExam)) { e.preventDefault(); otherSchoolGuard(guardedExam, () => navigate(`/worksheets/${examId}/edit`)); }
                    }} className="font-semibold text-ink underline underline-offset-[3px] hover:text-muted-2">Edit questions</Link>
                </p>
            )}

            {/* Marking is teacher/school-admin only (counselors get 403 from the endpoint). */}
            {isNewKind && !isSharedForeign && examId && role !== 'counselor' && <MarkingCard examId={Number(examId)} />}

            {isReadOnly && (
                <p role="note" className="mb-[18px] rounded-[14px] bg-mist px-4 py-3 text-sm text-ink">
                    {notMine
                        ? <>{(examData as any)?.exam?.creator_name || 'Another teacher'} created this {examKind}. Only they or a school admin can change it.</>
                        : <>Shared national exam from {sharedInstitutionName}. You can view it but not change it.</>}
                </p>
            )}

            <div className="mb-[18px] grid gap-[18px] min-[1180px]:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
                <section aria-label="Exam overview" className={CARD}>
                    <div className="mb-[18px] flex flex-wrap items-center justify-between gap-3">
                        <h2 className={CARD_TITLE}>Overview</h2>
                        <div className="flex flex-wrap items-center gap-2">
                            {examLoaded && exam.name && <KindBadge kind={examKind} />}
                            {examLoaded && <Badge tone={statusSwitch ? 'success' : 'draft'}>{statusSwitch ? 'Active' : 'Draft'}</Badge>}
                        </div>
                    </div>

                    {examError != null ? (
                        <div role="alert" className="rounded-[12px] bg-danger-bg p-4 text-danger-ink">Failed to load exam information.</div>
                    ) : examStatus === 'success' ? (
                        <>
                            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                                {isUniversity ? (
                                    <>
                                        <InfoTile label="Field of study">{exam?.field_of_study?.name || 'N/A'}</InfoTile>
                                        <InfoTile label="Academic year">{exam?.academic_year?.name || 'N/A'}</InfoTile>
                                        <InfoTile label="Courses" className="col-span-2">
                                            {exam?.courses?.length > 0 ? (
                                                <span className="flex flex-wrap gap-1.5">
                                                    {exam.courses.map((course: any, idx: number) => (
                                                        <Badge key={idx} tone="info">{course.name}</Badge>
                                                    ))}
                                                </span>
                                            ) : 'N/A'}
                                        </InfoTile>
                                        <InfoTile label="Tag">{exam?.tag?.name || 'N/A'}</InfoTile>
                                    </>
                                ) : (
                                    <>
                                        <InfoTile label="Subject">{exam?.subject || 'N/A'}</InfoTile>
                                        <InfoTile label="Grade">{gradeLabel(exam?.school_grade_name ?? exam?.grade)}</InfoTile>
                                        <InfoTile label="Duration">{exam?.time || '0'} minutes</InfoTile>
                                        <InfoTile label="Tag">{exam?.tag?.name || 'N/A'}</InfoTile>
                                    </>
                                )}
                            </div>

                            {exam && (
                                <div className="mt-5">
                                    <p className={`${SECTION_LABEL} mb-2.5`}>Assignment</p>
                                    <ExamAssignmentInfo
                                        dueDate={exam.due_date ?? null}
                                        dueAt={exam.due_at ?? null}
                                        assignmentScope={((exam.assignment_scope ?? 'grade_subject') as ExamAssignmentScope)}
                                        assignedStudentCount={(exam.assigned_student_ids ?? []).length}
                                        sections={exam.assigned_sections ?? []}
                                        instructions={exam.instructions ?? ''}
                                        gradeName={exam.school_grade?.name ?? exam.grade_name ?? exam.grade ?? null}
                                        subjectName={exam.school_subject?.name ?? exam.subject_name ?? exam.subject ?? null}
                                        onEdit={isReadOnly || !canEditAssignment ? undefined : guarded(openAssignEdit)}
                                    />
                                </div>
                            )}

                            <div className="mt-5 flex flex-wrap items-center gap-3.5 border-t border-hairline pt-4">
                                {createdLine && <span className="text-[13px] text-muted">{createdLine}</span>}
                                <span className="flex-1" />
                                {deliveryLabel && <Tag>{deliveryLabel}</Tag>}
                                <Tag>Chapters: {chapterCount}</Tag>
                            </div>
                        </>
                    ) : (
                        <div className="flex items-center justify-center p-8">
                            <Spinner size="md" className="text-muted" />
                        </div>
                    )}
                </section>

                <section aria-label="Publishing" className="relative flex min-w-0 flex-col gap-4 overflow-hidden rounded-[22px] bg-navy p-6 text-white">
                    <p className="font-mono text-[11px] font-semibold uppercase tracking-[0.08em] text-on-navy-muted">Publishing</p>
                    <div className="flex items-center gap-3.5 rounded-[14px] bg-navy-2 p-3.5">
                        <span className="min-w-0 flex-1">
                            <span className="block font-semibold">{statusSwitch ? 'Active' : 'Draft'}</span>
                            <span className="text-[13px] text-on-navy-muted">{statusSwitch ? 'Assigned students can take it now' : 'Only staff can see it'}</span>
                        </span>
                        {!examLoaded || isReadOnly ? null : isExamStatusChanging || isStatFetching ? (
                            <Spinner size="md" className="text-white" />
                        ) : (
                            <Switch onNavy checked={statusSwitch} onChange={guarded(changingExamStatus)} label="Exam is active" />
                        )}
                    </div>
                    {releaseMode && (
                        <div>
                            <p className="mb-2 font-semibold">Results for students</p>
                            <div role="group" aria-label="Release results" className="flex gap-0.5 rounded-[12px] border border-line-strong bg-surface p-1">
                                {RELEASE_OPTIONS.map((o) => {
                                    const on = o.key === releaseMode;
                                    return (
                                        <button
                                            key={o.key}
                                            type="button"
                                            aria-pressed={on}
                                            disabled={!(examLoaded && canEditAssignment && !isReadOnly) || isSavingAssign}
                                            onClick={() => { if (!on) guarded(changeRelease)(o.key); }}
                                            className={`min-h-[36px] flex-1 rounded-[8px] px-[13px] font-mono text-[13px] font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent disabled:cursor-default ${on ? 'bg-navy text-white' : 'bg-transparent text-muted enabled:hover:text-ink'}`}
                                        >
                                            {o.label}
                                        </button>
                                    );
                                })}
                            </div>
                            <p className="mt-2 text-[13px] text-on-navy-muted">{releaseNote(releaseMode, exam?.due_at, dueText)}</p>
                            {releaseError && <p role="alert" className="mt-2 text-[13px] font-semibold text-critical-soft">{releaseError}</p>}
                        </div>
                    )}
                    {takenHeader && takenHeader.assigned_count > 0 && (
                        <div>
                            <div className="mb-2 flex justify-between"><span className="font-semibold">Taken so far</span><span className="font-mono font-medium">{takenHeader.took_count} / {takenHeader.assigned_count}</span></div>
                            <div aria-hidden="true" className="h-2 overflow-hidden rounded-full bg-navy-3">
                                <div className="h-full rounded-full bg-on-navy-faint" style={{ width: `${Math.min(100, (takenHeader.took_count / takenHeader.assigned_count) * 100)}%` }} />
                            </div>
                            <p className="mt-2 text-[13px] text-on-navy-muted">
                                {[
                                    takenHeader.avg_score != null ? `Average so far ${takenHeader.avg_score.toFixed(1)}%` : null,
                                    behindLine,
                                ].filter(Boolean).join(' · ') || 'Nobody has taken it yet'}
                            </p>
                        </div>
                    )}
                    <Link to={`/exams/report/${examId}`} className="mt-auto inline-flex min-h-[44px] items-center justify-center gap-2 rounded-[12px] border border-line-strong bg-surface px-4 text-sm font-semibold text-ink transition hover:bg-surface-2 hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent">
                        Open full report <ArrowRight size={18} strokeWidth={1.8} aria-hidden />
                    </Link>
                </section>
            </div>

            <section aria-label="Questions" className={CARD}>
                <div className="mb-[18px] flex flex-wrap items-start justify-between gap-3">
                    <div className="min-w-0">
                        <h2 className={CARD_TITLE}>Questions</h2>
                        <p className="mt-1 text-[13px] text-muted">{countsLine}</p>
                        {canEditExam && (
                            <p className="mt-0.5 text-[13px] text-muted">
                                {selectedCount > 0 ? `${selectedCount} question${selectedCount === 1 ? '' : 's'} selected` : 'Select one or more questions to assign a shared note.'}
                            </p>
                        )}
                    </div>
                    {canEditExam && (
                        <div className="flex flex-wrap items-center gap-2.5">
                            <span className="inline-flex items-center gap-2.5 text-[13px] font-semibold text-ink">
                                Chapter edit
                                <Switch checked={chapterEdit} onChange={guarded(() => changeSetChapterEdit())} label="Chapter edit" />
                            </span>
                            <KitButton variant="ghost" icon={<StickyNote size={18} strokeWidth={1.8} aria-hidden />} disabled={selectedCount === 0} onClick={guarded(() => setSharedNoteModalOpen(true))}>
                                {selectedCount > 0 ? `Assign shared note (${selectedCount})` : 'Assign shared note'}
                            </KitButton>
                            <KitButton variant="ghost" aria-label="Add questions to this exam" icon={<Plus size={18} strokeWidth={1.8} aria-hidden />} onClick={guarded(() => setAddQuestionsOpen(true))}>
                                Add questions
                            </KitButton>
                        </div>
                    )}
                </div>

                {canEditExam && (
                    <div className="mb-4 flex items-center gap-3.5 rounded-[16px] border border-track bg-panel p-3.5">
                        <span className="grid h-10 w-10 flex-none place-items-center rounded-[14px] bg-mist text-slate">
                            <Sparkles size={18} strokeWidth={1.8} aria-hidden />
                        </span>
                        <span className="min-w-0 flex-1">
                            <span className="block font-semibold text-ink">AI question processing</span>
                            <span className="text-[13px] text-muted">Use AI to fix explanations and check answer keys against your textbooks</span>
                        </span>
                        <Switch checked={questionProcessingMode} onChange={guarded(() => setQuestionProcessingMode(!questionProcessingMode))} label="AI question processing mode" />
                    </div>
                )}

                {canEditExam && questionProcessingMode && (
                    <div className="mb-4 rounded-[16px] border border-dashed border-control bg-note p-3.5">
                        <div className="flex flex-wrap items-center gap-2.5">
                            <span className={`${SECTION_LABEL} mr-1`}>
                                {selectedCount > 0 ? `Run on ${selectedCount} selected` : `Run on all ${pipelineQuestions.length}`}
                            </span>
                            <KitButton
                                variant="ghost" size="sm"
                                disabled={isLoadingRags || useOptions === null}
                                icon={pipelineProgress.phase === 'rag' ? <Spinner size="sm" /> : <Search size={18} strokeWidth={1.8} aria-hidden />}
                                onClick={() => {
                                    const indices = selectedQuestionIds.length > 0
                                        ? selectedQuestionIds.map(id => pipelineQuestions.findIndex(q => String(q.id) === String(id))).filter(idx => idx !== -1)
                                        : pipelineQuestions.map((_, i) => i);
                                    if (indices.length > 0) handleSearchByCourseBulk(indices);
                                }}
                            >
                                Search by course
                            </KitButton>
                            <KitButton
                                variant="ghost" size="sm"
                                disabled={isLoadingRags || allRags.length === 0}
                                icon={(pipelineProgress.phase === 'rag' || pipelineProgress.phase === 'explanation') ? <Spinner size="sm" /> : <Lightbulb size={18} strokeWidth={1.8} aria-hidden />}
                                onClick={() => {
                                    const indices = selectedQuestionIds.length > 0
                                        ? selectedQuestionIds.map(id => pipelineQuestions.findIndex(q => String(q.id) === String(id))).filter(idx => idx !== -1)
                                        : pipelineQuestions.map((_, i) => i);
                                    if (indices.length > 0) handleAIAssistedGenerationBulk(indices);
                                }}
                            >
                                AI assisted generation
                            </KitButton>
                            <KitButton
                                variant="ghost" size="sm"
                                disabled={isLoadingRags}
                                icon={isLoadingRags ? <Spinner size="sm" /> : <Search size={18} strokeWidth={1.8} aria-hidden />}
                                onClick={() => {
                                    const indices = selectedQuestionIds.length > 0
                                        ? selectedQuestionIds.map(id => pipelineQuestions.findIndex(q => String(q.id) === String(id))).filter(idx => idx !== -1)
                                        : pipelineQuestions.map((_, i) => i);
                                    if (indices.length > 0) retryNoContextAllBooks(indices, allRags);
                                }}
                            >
                                Search all books
                            </KitButton>
                            <KitButton
                                variant="ghost" size="sm"
                                disabled={isLoadingRags}
                                icon={<BookOpen size={18} strokeWidth={1.8} aria-hidden />}
                                onClick={() => {
                                    const indices = selectedQuestionIds.length > 0
                                        ? selectedQuestionIds.map(id => pipelineQuestions.findIndex(q => String(q.id) === String(id))).filter(idx => idx !== -1)
                                        : pipelineQuestions.map((_, i) => i);
                                    if (indices.length > 0) handleSearchSpecificBooksBulk(indices);
                                }}
                            >
                                Search specific books
                            </KitButton>
                            <KitButton
                                variant="ghost" size="sm"
                                icon={<GraduationCap size={18} strokeWidth={1.8} aria-hidden />}
                                onClick={() => {
                                    const indices = selectedQuestionIds.length > 0
                                        ? selectedQuestionIds.map(id => pipelineQuestions.findIndex(q => String(q.id) === String(id))).filter(idx => idx !== -1)
                                        : pipelineQuestions.map((_, i) => i);
                                    if (indices.length > 0) useAIKnowledge(indices);
                                }}
                            >
                                Use AI knowledge
                            </KitButton>
                            <KitButton
                                variant="ghost" size="sm"
                                disabled={useOptions === null}
                                icon={<Lightbulb size={18} strokeWidth={1.8} aria-hidden />}
                                onClick={() => {
                                    const indices = selectedQuestionIds.length > 0
                                        ? selectedQuestionIds.map(id => pipelineQuestions.findIndex(q => String(q.id) === String(id))).filter(idx => idx !== -1)
                                        : pipelineQuestions.map((_, i) => i);
                                    if (indices.length > 0) handleReassignChapterBulk(indices);
                                }}
                            >
                                Reassign chapter
                            </KitButton>
                            <span className="flex-1" />
                            {aiSummary && <span className="text-[13px] text-muted">{aiSummary}</span>}
                            <KitButton
                                variant="secondary" size="sm"
                                loading={isSavingAll}
                                disabled={isSavingAll || pipelineQuestions.every(q => q._status !== 'explanation_done' && q._status !== 'no_context')}
                                icon={<Check size={18} strokeWidth={1.8} aria-hidden />}
                                onClick={handleSaveAllAISuggestions}
                            >
                                Save all AI results
                            </KitButton>
                        </div>
                        {pipelineProgress.phase !== 'idle' && pipelineProgress.phase !== 'done' && (
                            <div className="mt-3.5 space-y-2">
                                <div className="flex flex-wrap items-center justify-between gap-2 text-xs font-semibold">
                                    <span className="text-ink">{pipelineProgress.message}</span>
                                    <div className="flex items-center gap-3 font-mono">
                                        <span className="text-success">{pipelineProgress.completed} done</span>
                                        {pipelineProgress.failed > 0 && <span className="text-danger">{pipelineProgress.failed} failed</span>}
                                        <span className="text-muted">{Math.round((pipelineProgress.completed / pipelineProgress.total) * 100)}%</span>
                                        <span className="text-muted">{pipelineProgress.completed}/{pipelineProgress.total}</span>
                                    </div>
                                </div>
                                <Track value={(pipelineProgress.completed / pipelineProgress.total) * 100} label="AI processing progress" />
                            </div>
                        )}
                    </div>
                )}

                <Tabs<'questions' | 'stats'>
                    items={[
                        { key: 'questions', label: 'Questions', ...(isNewKind ? {} : { count: questionSource.length }) },
                        { key: 'stats', label: 'Statistics', ...(isNewKind ? {} : { count: questionSource.length }) },
                    ]}
                    value={detailTab}
                    onChange={setDetailTab}
                />

                {/* Kept mounted while hidden so KaTeX auto-render output survives a tab switch. */}
                <div hidden={detailTab !== 'questions'}>
                    <div ref={containerRef}>
                        {isNewKind ? (
                            <WorksheetQuestionsPreview examId={Number(examId)} kind={examKind} canEdit={!isReadOnly && role !== 'counselor'} />
                        ) : questionSource.length > 0 ? (
                            <>
                                {showAllQuestions ? questionsArray : questionsArray.slice(0, QUESTIONS_PREVIEW)}
                                <ShowMoreFooter
                                    shown={showAllQuestions ? questionsArray.length : Math.min(QUESTIONS_PREVIEW, questionsArray.length)}
                                    total={questionsArray.length}
                                    expanded={showAllQuestions}
                                    onToggle={() => setShowAllQuestions((v) => !v)}
                                />
                            </>
                        ) : questionIsFetching ? (
                            <div className="flex justify-center py-10"><Spinner size="md" className="text-muted" /></div>
                        ) : questionsIsError ? (
                            <p role="alert" className="rounded-[12px] bg-danger-bg px-4 py-3 text-sm text-danger-ink">Could not load the questions for this exam.</p>
                        ) : (
                            <EmptyState compact icon={<ListChecks size={18} strokeWidth={1.8} aria-hidden />} title="No questions yet" body={canEditExam ? 'Add questions to this exam to see them here.' : undefined} />
                        )}
                    </div>
                    {chapterEdit && !isReadOnly && (
                        <div className="mt-2 flex flex-wrap items-center justify-end gap-3 border-t border-hairline pt-4">
                            {Object.keys(questionChapterChanged).length === 0 && (
                                <span className="text-[13px] text-muted">Pick a new chapter for at least one question to save.</span>
                            )}
                            <KitButton
                                variant="secondary"
                                icon={<Check size={18} strokeWidth={1.8} aria-hidden />}
                                disabled={Object.keys(questionChapterChanged).length === 0}
                                onClick={() => setOpenVerificationModal(true)}
                            >
                                Save chapter changes
                            </KitButton>
                        </div>
                    )}
                </div>

                {detailTab === 'stats' && <ExamQuestionStats examId={Number(examId)} />}
            </section>

            <EditAssignmentModal
                isOpen={assignOpen}
                onClose={() => setAssignOpen(false)}
                exam={exam}
                saving={isSavingAssign}
                save={(payload) => updateAssignmentMut({ exam_id: examId!, payload })}
            />

            <Modal show={editInfoOpen} size="md" onClose={() => setEditInfoOpen(false)} popup>
                <Modal.Header />
                <Modal.Body>
                    <div className="space-y-4">
                        <h3 className="font-display text-lg font-bold text-ink">Edit exam info</h3>
                        <div>
                            <Label htmlFor="examName" value="Exam name" className={`${MODAL_LABEL} mb-2 block`} />
                            <input id="examName" title="Exam name" placeholder="Exam name" type="text" value={editName} onChange={(e) => setEditName(e.target.value)} className={INPUT} />
                        </div>
                        <div>
                            <Label htmlFor="examTime" value="Time (minutes)" className={`${MODAL_LABEL} mb-2 block`} />
                            <input id="examTime" title="Time (minutes)" placeholder="Time in minutes" type="number" min={0} value={editMinutes} onChange={(e) => setEditMinutes(e.target.value)} className={INPUT} />
                        </div>
                        <div>
                            <Label htmlFor="examTag" value="Exam tag" className={`${MODAL_LABEL} mb-2 block`} />
                            <select id="examTag" value={editTag} onChange={(e) => setEditTag(e.target.value)} className={INPUT}>
                                <option value="">No tag</option>
                                {tagsData?.map((tag: any) => (
                                    <option key={tag.id} value={tag.id}>{tag.name}</option>
                                ))}
                            </select>
                        </div>
                        <div className="mt-6 flex justify-end gap-3">
                            <KitButton variant="ghost" onClick={() => setEditInfoOpen(false)}>Cancel</KitButton>
                            <KitButton onClick={saveEditInfo} loading={isEditing}>Save changes</KitButton>
                        </div>
                    </div>
                </Modal.Body>
            </Modal>

            <Modal show={openVerificationModal} size="md" onClose={() => setOpenVerificationModal(false)} popup>
                <Modal.Header />
                <Modal.Body>
                    <div className="p-6 text-center">
                        <span className="mx-auto mb-4 grid h-[52px] w-[52px] place-items-center rounded-[16px] bg-mist text-slate">
                            <Info size={18} strokeWidth={1.8} aria-hidden />
                        </span>
                        <h3 className="mb-2 font-display text-xl font-bold text-ink">
                            Confirm changes
                        </h3>
                        <p className="mb-6 text-muted">
                            Are you sure you want to save these chapter assignments?
                        </p>
                        <div className="flex justify-center gap-4">
                            <KitButton onClick={(e) => changingChpater(e)}>
                                Yes, save changes
                            </KitButton>
                            <KitButton variant="ghost" onClick={() => setOpenVerificationModal(false)}>
                                Cancel
                            </KitButton>
                        </div>
                    </div>
                </Modal.Body>
            </Modal>

            <Modal show={chapterGradeModalOpen} size="md" onClose={() => setChapterGradeModalOpen(false)} popup>
                <Modal.Header/>
                <Modal.Body>
                    <div className="space-y-6 p-4">
                        <div>
                            <h3 className="font-display text-xl font-bold text-ink">Filter chapters</h3>
                            <p className="text-sm text-muted">Select grades to include chapters from</p>
                        </div>

                        <div className="flex gap-2">
                            <button type="button" className="text-[13px] font-semibold text-ink hover:underline" onClick={selectAllGrades}>Select all</button>
                            <span className="text-line-strong" aria-hidden>|</span>
                            <button type="button" className="text-[13px] font-semibold text-muted hover:underline" onClick={clearAllGrades}>Clear all</button>
                        </div>

                        <div className="max-h-60 overflow-y-auto rounded-[14px] border border-line bg-surface-2 p-4">
                            {gradeOptions.length === 0 ? (
                                <p className="py-4 text-center text-sm text-muted">No grades found for this subject.</p>
                            ) : (
                                <div className="grid grid-cols-1 gap-2">
                                    {gradeOptions.map(opt => (
                                        <label key={opt.id} className="flex cursor-pointer items-center gap-3 rounded-[10px] p-2 transition-colors hover:bg-surface">
                                            <input
                                                type="checkbox"
                                                checked={!!selectedGradeIds?.includes(opt.id)}
                                                onChange={() => toggleGrade(opt.id)}
                                                className={CHECKBOX}
                                            />
                                            <span className="text-sm font-medium text-ink">{opt.name}</span>
                                        </label>
                                    ))}
                                </div>
                            )}
                        </div>

                        <div className="flex justify-end gap-3 pt-4">
                            <KitButton variant="ghost" onClick={() => {
                                setSelectedGradeIds([...appliedGradeIds]);
                                setChapterGradeModalOpen(false);
                            }}>Cancel</KitButton>
                            <KitButton onClick={() => {
                                setAppliedGradeIds([...selectedGradeIds]);
                                setChapterGradeModalOpen(false);
                            }}>Apply filters</KitButton>
                        </div>
                    </div>
                </Modal.Body>
            </Modal>

            {/* Book Picker Dialog */}
            {bookPickerOpen && (
                <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-navy/50" onClick={() => setBookPickerOpen(false)}>
                    <div role="dialog" aria-modal="true" aria-label="Select books to search" className="mx-4 flex max-h-[80vh] w-full max-w-lg flex-col rounded-[20px] border border-line bg-surface shadow-card" onClick={e => e.stopPropagation()}>
                        <div className="border-b border-line px-6 py-4">
                            <h3 className="font-display text-lg font-bold text-ink">Select books to search</h3>
                            <p className="mt-1 text-[13px] text-muted">{bookPickerIndices.length === 1 ? `Choose books to search for Q${bookPickerIndices[0] + 1}` : `Choose books to search for ${bookPickerIndices.length} questions`}. 5 results per book will be retrieved.</p>
                        </div>
                        <div className="flex-1 space-y-2 overflow-y-auto px-6 py-3">
                            {allRags.length === 0 && <p className="text-sm text-muted">No books available.</p>}
                            {allRags.map((rag: any) => {
                                const id = String(rag.id)
                                const checked = selectedBookIds.includes(id)
                                return (
                                    <label key={id} className={`flex cursor-pointer items-start gap-3 rounded-[12px] border p-3 transition-colors ${checked ? 'border-slate bg-mist' : 'border-line hover:bg-surface-2'}`}>
                                        <input
                                            type="checkbox"
                                            checked={checked}
                                            onChange={() => setSelectedBookIds(prev => checked ? prev.filter(b => b !== id) : [...prev, id])}
                                            className={`${CHECKBOX} mt-0.5`}
                                        />
                                        <div className="min-w-0">
                                            <div className="truncate text-sm font-medium text-ink">{rag.title || rag.name || `Book ${id}`}</div>
                                            {rag.course_names?.length > 0 && (
                                                <div className="mt-0.5 text-xs text-muted">{rag.course_names.join(', ')}</div>
                                            )}
                                        </div>
                                    </label>
                                )
                            })}
                        </div>
                        <div className="flex items-center justify-between border-t border-line px-6 py-4">
                            <span className="text-xs text-muted">{selectedBookIds.length} book(s) selected</span>
                            <div className="flex gap-2">
                                <KitButton size="sm" variant="ghost" onClick={() => setBookPickerOpen(false)}>Cancel</KitButton>
                                <KitButton size="sm" variant="secondary" disabled={selectedBookIds.length === 0} onClick={handleBookPickerConfirm}>
                                    Search selected books
                                </KitButton>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <Modal show={sharedNoteModalOpen} size="2xl" onClose={() => setSharedNoteModalOpen(false)}>
                <Modal.Header>Assign shared note</Modal.Header>
                <Modal.Body>
                    <div className="space-y-4">
                        <p className="text-sm text-muted">
                            You are assigning a note to {selectedQuestionIds.length} selected questions.
                        </p>

                        <div className="mb-4 border-b border-line pb-4">
                            <Label htmlFor="existing_note" value="Select existing note" className={`${MODAL_LABEL} mb-2 block`} />
                            <select
                                id="existing_note"
                                value={existingSharedNoteId}
                                onChange={(e) => setExistingSharedNoteId(e.target.value)}
                                className={INPUT}
                            >
                                <option value="">-- Create new note --</option>
                                {sharedNotesData?.map((note: any) => (
                                    <option key={note.id} value={note.id}>{note.title}</option>
                                ))}
                            </select>
                        </div>

                        {!existingSharedNoteId && (
                            <div className="space-y-4">
                                <div>
                                    <Label htmlFor="note_title" value="Note title" className={`${MODAL_LABEL} mb-2 block`} />
                                    <input
                                        id="note_title"
                                        title="Note title"
                                        className={INPUT}
                                        value={newSharedNote.title}
                                        onChange={(e) => setNewSharedNote({ ...newSharedNote, title: e.target.value })}
                                        placeholder="e.g. Reading Passage 1"
                                    />
                                </div>
                                <div>
                                    <Label htmlFor="note_content" value="Note content (large text/HTML)" className={`${MODAL_LABEL} mb-2 block`} />
                                    <RichSlateEditor
                                        initialValue={newSharedNote.content}
                                        onChange={(html) => setNewSharedNote({ ...newSharedNote, content: html })}
                                    />
                                </div>
                            </div>
                        )}
                    </div>
                </Modal.Body>
                <Modal.Footer className="flex justify-end gap-3">
                    <KitButton variant="ghost" onClick={() => setSharedNoteModalOpen(false)}>
                        Cancel
                    </KitButton>
                    <KitButton
                        onClick={handleAssignSharedNote}
                        disabled={!existingSharedNoteId && (!newSharedNote.title || !newSharedNote.content)}
                        loading={createSharedNoteMut.isPending || assignSharedNoteMut.isPending}
                    >
                        Apply to selected questions
                    </KitButton>
                </Modal.Footer>
            </Modal>

            {otherSchoolDialog}

            <ConfirmDialog
                isOpen={questionToDelete != null}
                title="Delete this question?"
                body="The question is removed from this exam. This cannot be undone."
                confirmLabel="Delete question"
                onConfirm={confirmDeleteQuestion}
                onCancel={() => setQuestionToDelete(null)}
            />

            <AddOtherQuestionsDialog
                show={addQuestionsOpen}
                onClose={() => setAddQuestionsOpen(false)}
                examId={examId!}
                onQuestionsAdded={() => {
                    refetchQuestions();
                    setAddQuestionsOpen(false);
                }}
            />

            <Modal show={editSharedNoteModalOpen} size="2xl" onClose={() => setEditSharedNoteModalOpen(false)}>
                <Modal.Header>Edit shared note</Modal.Header>
                <Modal.Body>
                    {editingSharedNote && (
                        <div className="space-y-4">
                            <div>
                                <Label htmlFor="edit_note_title" value="Note title" className={`${MODAL_LABEL} mb-2 block`} />
                                <input
                                    id="edit_note_title"
                                    title="Note title"
                                    className={INPUT}
                                    value={editingSharedNote.title}
                                    onChange={(e) => setEditingSharedNote({ ...editingSharedNote, title: e.target.value })}
                                />
                            </div>
                            <div>
                                <Label htmlFor="edit_note_content" value="Note content" className={`${MODAL_LABEL} mb-2 block`} />
                                <RichSlateEditor
                                    initialValue={editingSharedNote.content}
                                    onChange={(html) => setEditingSharedNote({ ...editingSharedNote, content: html })}
                                />
                            </div>
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer className="flex justify-end gap-3">
                    <KitButton variant="ghost" onClick={() => setEditSharedNoteModalOpen(false)}>
                        Cancel
                    </KitButton>
                    <KitButton
                        onClick={handleUpdateSharedNote}
                        disabled={!editingSharedNote?.title || !editingSharedNote?.content}
                        loading={updateSharedNoteMut.isPending}
                    >
                        Update note
                    </KitButton>
                </Modal.Footer>
            </Modal>
        </>
    )
}

export default ExamDetailComponent
